import { X, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog';
import { getValidImgVariant } from '@/utils';
import { ProductUpdatePopupDto } from '@sportspredict/shared';

interface ProductUpdatePopupProps {
	popup: ProductUpdatePopupDto;
	open: boolean;
	onClose: () => void;
}

function formatPopupDate(date: Date | string): string {
	return new Date(date)
		.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
		.toUpperCase();
}

type PopupSection = {
	id: string;
	cardTitle: string;
	iconImgVariants: { l?: string; m?: string; s?: string } | null;
	bullets: string[];
};

export default function ProductUpdatePopup({ popup, open, onClose }: ProductUpdatePopupProps) {
	const sections = (popup.sections ?? []) as PopupSection[];

	return (
		<Dialog
			open={open}
			onOpenChange={(isOpen) => {
				if (!isOpen) onClose();
			}}
		>
			<DialogContent
				hideCancelIcon
				overlayClassName="!z-[110]"
				className="w-[593px] max-w-[calc(100%-32px)] p-0 gap-0 flex flex-col !rounded-[24px] max-h-[635px] overflow-hidden !border-0 !z-[110]"
				style={{
					background: 'linear-gradient(180.02deg, #1F488F -13.23%, #1C2E4E 42.97%, #151B2E 99.99%)',
				}}
			>
				<DialogTitle className="sr-only">Product Updates</DialogTitle>

				<div className="overflow-y-auto flex flex-col [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#ACC0F6]/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#ACC0F6]/50">
					{/* Header */}
					<div className="pt-[60px] px-5 pb-4 text-center relative flex-shrink-0">
						<DialogClose
							onClick={onClose}
							className="absolute right-3 top-3 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none"
						>
							<X className="w-5 h-5 text-white" />
						</DialogClose>

						<span
							className="inline-flex items-center justify-center h-[26px] px-3 rounded-[8px] text-white font-['DM_Sans'] font-semibold text-[12px] leading-[18px] tracking-[0.08em] uppercase"
							style={{ background: '#5583F63D', border: '0.5px solid #1F4A94' }}
						>
							{popup.badgeText}
						</span>
						<h2 className="mt-2 text-[24px] font-bold text-[#F7F7F8] leading-[150%] tracking-[0.008em] text-center ![font-family:Orbitron]">
							{popup.title}
						</h2>
						<p className="mt-1 text-[12px] font-normal text-[#ACC0F6] leading-[24px] tracking-[0.08em] text-center font-['DM_Sans']">
							{formatPopupDate(popup.date)}
						</p>
					</div>

					<div className="px-5 pb-4">
						{sections.map((section) => {
							const iconUrl = getValidImgVariant(section.iconImgVariants);
							return (
								<div
									key={section.id}
									className="mb-3 last:mb-0 rounded-[12px] p-[16px]"
									style={{ background: '#1C2D4AA3', border: '0.5px solid #ACC0F63D' }}
								>
									<div className="flex flex-col">
										<div
											className="flex gap-[14px] items-center pb-[13px]"
											style={{ borderBottom: '0.5px solid #ACC0F63D' }}
										>
											<div
												className="w-10 h-10 flex-shrink-0 bg-[#1D56C8] flex items-center justify-center"
												style={{ borderRadius: '28px' }}
											>
												{iconUrl && (
													<img
														src={iconUrl}
														alt={section.cardTitle}
														className="w-5 h-5 object-contain"
													/>
												)}
											</div>
											<h3 className="text-[18px] font-bold text-[#F7F7F8] leading-[24px] tracking-[-0.028em] font-['DM_Sans']">
												{section.cardTitle}
											</h3>
										</div>
										<ul className="flex flex-col gap-[12px] mt-2">
											{(section.bullets ?? []).map((bullet, i) => (
												<li
													key={i}
													className="text-[14px] font-normal text-[#E5E5E5] leading-[24px] tracking-[0.012em] font-['DM_Sans'] before:content-['•'] before:mr-1.5"
												>
													{bullet}
												</li>
											))}
										</ul>
									</div>
								</div>
							);
						})}
					</div>

					{/* Footer */}
					<div className="px-5 pb-5">
						<div
							className="flex items-center justify-between gap-3 rounded-[12px] p-[16px]"
							style={{ background: '#1C2D4AA3', border: '0.5px solid #ACC0F63D' }}
						>
							<div>
								<p className="text-[12px] font-semibold text-[#F7F7F8]">Questions or ideas?</p>
								<p className="text-[11px] text-[#ACC0F6]">We'd love to hear from you.</p>
							</div>
							<a
								href="support@sportspredict.com"
								className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2667FF] hover:bg-[#1a4fd6] transition-colors text-[13px] font-bold text-white whitespace-nowrap"
							>
								Email Us
								<Mail className="w-3.5 h-3.5" />
							</a>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
